// Mapping between API product codes and legacy product keys used in the system
// Only include mappings where the legacy key is different from the product code
export const PRODUCT_CODE_TO_LEGACY_KEY: {[key: string]: string} = {
    // F&G products
    FGIUL: 'FGPathsetterIUL', // Corrected from FGPIUL to FGIUL
    FGEVL: 'FGEverlast',

    // Prosper
    PROSPERIUL: 'Prosper',

    // Equitable
    EQVUL: 'EquitableVUL',
};

// Reverse mapping from legacy keys to product codes
export const LEGACY_KEY_TO_PRODUCT_CODE: {[key: string]: string} = Object.entries(PRODUCT_CODE_TO_LEGACY_KEY).reduce(
    (acc, [code, key]) => {
        // Handle special case where multiple codes map to same key
        if (!acc[key]) {
            acc[key] = code;
        }
        return acc;
    },
    {} as {[key: string]: string},
);

/**
 * Convert product codes to legacy keys
 * @param productCodes Array of product codes from API
 * @returns Array of legacy keys for backward compatibility, or the original code if no mapping exists
 */
export function productCodesToLegacyKeys(productCodes: string[]): string[] {
    return productCodes
        .map((code) => {
            // If there's a mapping, use it. Otherwise, return the original code
            return PRODUCT_CODE_TO_LEGACY_KEY[code] || code;
        })
        .filter(Boolean);
}

/**
 * Convert legacy keys to product codes
 * @param legacyKeys Array of legacy product keys
 * @returns Array of product codes, or the original key if no mapping exists
 */
export function legacyKeysToProductCodes(legacyKeys: string[]): string[] {
    return legacyKeys
        .map((key) => {
            // If there's a mapping, use it. Otherwise, return the original key
            return LEGACY_KEY_TO_PRODUCT_CODE[key] || key;
        })
        .filter(Boolean);
}

/**
 * Check if a product code corresponds to a legacy key
 * @param productCode Product code from API
 * @param legacyKey Legacy product key
 * @returns true if they map to each other
 */
export function isMatchingProduct(productCode: string, legacyKey: string): boolean {
    return (
        PRODUCT_CODE_TO_LEGACY_KEY[productCode] === legacyKey || LEGACY_KEY_TO_PRODUCT_CODE[legacyKey] === productCode
    );
}

/**
 * Mapping from legacy keys to carrier names for firstCarrierQuoted field
 * This is used to convert internal product keys to the carrier names expected by the dropdown
 */
export const LEGACY_KEY_TO_CARRIER_NAME: {[key: string]: string} = {
    FGPathsetterIUL: 'F&G Pathsetter',
    FGEverlast: 'F&G Everlast',
    EquitableVUL: 'Equitable',
    Prosper: 'EMC National Life Company',
    MOOIULE: 'Mutual of Omaha',
};

/**
 * Convert legacy key to carrier name for display
 */
export function legacyKeyToCarrierName(legacyKey: string): string {
    return LEGACY_KEY_TO_CARRIER_NAME[legacyKey] || legacyKey;
}

/**
 * Mapping from database product codes to quote carrier product dropdown values
 * Used for auto-selecting carrier products on the quotes page based on prequal selection
 */
export const PRODUCT_CODE_TO_CARRIER_PRODUCT: {[key: string]: {productType: string; carrierProduct: string}} = {
    // IUL Products
    FGIUL: {productType: 'IUL', carrierProduct: 'FGPathsetter'}, // F&G - IUL Pathsetter
    FGEVL: {productType: 'IUL', carrierProduct: 'FGEverlast'}, // F&G - Everlast
    AMIUL: {productType: 'PreApproval', carrierProduct: '139'}, // Ameritas IUL - disabled in quotes
    AMIULPRF: {productType: 'PreApproval', carrierProduct: '139'}, // Ameritas IUL Preferred - disabled in quotes
    OMEXPRIUL: {productType: 'PreApproval', carrierProduct: 'MOM'}, // Mutual of Omaha - IUL Express - disabled in quotes
    PROSPERIUL: {productType: 'Prosper', carrierProduct: 'Prosper'}, // Prosper IUL - has its own tab

    // LSW IUL (separate tab)
    LSWIUL: {productType: 'LSWIUL', carrierProduct: 'LSW'}, // LSW FlexLife
    LSWIULPRF: {productType: 'LSWIUL', carrierProduct: 'LSW'}, // LSW FlexLife Preferred

    // Term Products
    AMTERM: {productType: 'Term', carrierProduct: '163'}, // Ameritas: FLX Living Benefits Term
    AMTERMPRF: {productType: 'Term', carrierProduct: '163'}, // Ameritas: FLX Living Benefits Term (Preferred)
    LSWTERM: {productType: 'Term', carrierProduct: '1594'}, // National Life Group: LSW Term
    LSWTERMPRF: {productType: 'Term', carrierProduct: '1594'}, // National Life Group: LSW Term (Preferred)

    // VUL Products
    AMVUL: {productType: 'VUL', carrierProduct: 'Ameritas'}, // Ameritas VUL
    AMVULPRF: {productType: 'VUL', carrierProduct: 'Ameritas'}, // Ameritas VUL Preferred
    AMVULADJ: {productType: 'VUL', carrierProduct: 'Ameritas'}, // Adjusted Ameritas VUL (not recommended)
    EQVUL: {productType: 'VUL', carrierProduct: 'Equitable'}, // Equitable VUL

    // Whole Life Products (Final Expense)
    TRFEXP: {productType: 'Whole Life', carrierProduct: 'TAFEExpress'}, // Transamerica FE Express - goes to WHL tab

    // Guaranteed Products
    MOBAS: {productType: 'Guaranteed', carrierProduct: 'MOM'}, // Mutual of Omaha: Living Promise
    GWBAS: {productType: 'Guaranteed', carrierProduct: 'GreatWestern'}, // Great Western: Final Expense
    GERBAS: {productType: 'Guaranteed', carrierProduct: 'Gerber'}, // Gerber: Final Expense
    TRABAS: {productType: 'Guaranteed', carrierProduct: 'TRA'}, // Transamerica Easy Solution

    // ADB Products
    MOADBGAD: {productType: 'ADB', carrierProduct: 'MOOGuaranteedADvantage'},

    // Annuity Products
    LSWANNFSG: {productType: 'Annuity', carrierProduct: 'FlexSecureGrowth10'}, // National Life Group: Annuity
};

/**
 * Get the carrier product value for a given database product code
 * @param productCode Database product code
 * @returns Object with productType and carrierProduct value for quotes dropdown, or null if not mapped
 */
export function getCarrierProductFromCode(productCode: string): {productType: string; carrierProduct: string} | null {
    return PRODUCT_CODE_TO_CARRIER_PRODUCT[productCode] || null;
}

/**
 * Products that are disabled in the quotes dropdowns
 * These should go to pre-approvals instead
 */
export const DISABLED_QUOTE_PRODUCTS = {
    IUL: ['Ameritas: IUL  - FLX living benefits', 'MOO - IUL Express'],
    // Add other disabled products per tab as needed
};

/**
 * Check if a product would be disabled in quotes
 * @param productCode Database product code
 * @param productType Product type/tab
 * @returns true if the product would be disabled
 */
export function isProductDisabledInQuotes(productCode: string, productType: string): boolean {
    const mapping = PRODUCT_CODE_TO_CARRIER_PRODUCT[productCode];
    if (!mapping) return false;

    const disabledList = DISABLED_QUOTE_PRODUCTS[productType as keyof typeof DISABLED_QUOTE_PRODUCTS];
    if (!disabledList) return false;

    // Check if the carrier product name would be disabled
    // This would need to be adjusted based on how the options are structured
    return mapping.productType === 'PreApproval';
}
